import { call, fork, take, put } from 'redux-saga/effects'
import { bindAsyncAction } from 'typescript-fsa-redux-saga'
import { Action } from 'typescript-fsa'
import { GetHelpPostRequest } from '@professocialApi/professocial-core-frontend-api'
import {
  createEditorHelpPost,
  createEditorHelpPostInitiated, deleteEditorHelpPost, deleteEditorHelpPostInitiated,
  getEditorHelpPost, getEditorHelpPostInitiated,
  getHelpPost,
  getHelpPostInitiated,
  getHelpPostList,
  getHelpPostListInitiated,
  updateEditorHelpPost,
  updateEditorHelpPostInitiated,
} from './help.actions'
import { HelpROApi, EditorHelpRESTServiceApi } from '../../api/instances'
import {
  CreateEditorHelpPostRequest, DeleteEditorHelpPostRequest, GetEditorHelpPostRequest, UpdateEditorHelpPostRequest,
} from '@professocialApi/professocial-core-frontend-api/apis/EditorHelpRESTServiceApi'
import { requestFailed, requestFinished, requestStarted } from '../common/common.actions'
import { EventType, Severity } from '@professocial/professocialTypes/types'
import i18n from 'i18next'

// getHelpPost
const getHelpPostWorker = bindAsyncAction(getHelpPost)(
  function * (params: GetHelpPostRequest) {
    return yield call([HelpROApi, HelpROApi.getHelpPost], params)
  },
)

function * watchGetHelpPostInitiated () {
  while (true) {
    const action: Action<GetHelpPostRequest> = yield take(getHelpPostInitiated)
    try {
      yield getHelpPostWorker(action.payload)
    } catch (e) {
      console.error('error in watchGetHelpPostInitiated: ', e)
    }
  }
}

// getHelpPostList
const getHelpPostListWorker = bindAsyncAction(getHelpPostList)(
  function * () {
    return yield call([HelpROApi, HelpROApi.getHelpPostList])
  },
)

function * watchGetHelpPostListInitiated () {
  while (true) {
    const action: Action<undefined> = yield take(getHelpPostListInitiated)
    try {
      yield getHelpPostListWorker(action.payload)
    } catch (e) {
      console.error('error in watchGetHelpPostListInitiated: ', e)
    }
  }
}

// createEditorHelpPost
const createEditorHelpPostWorker = bindAsyncAction(createEditorHelpPost)(
  function * (params: CreateEditorHelpPostRequest) {
    return yield call([EditorHelpRESTServiceApi, EditorHelpRESTServiceApi.createEditorHelpPost], params)
  },
)

function * watchCreateEditorHelpPostInitiated () {
  while (true) {
    const action: Action<CreateEditorHelpPostRequest> = yield take(createEditorHelpPostInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield createEditorHelpPostWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        message: {
          severity: Severity.success,
          message: i18n.t('coreHelp:createHelpPostSuccess'),
          eventType: EventType.create,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        message: {
          severity: Severity.error,
          message: i18n.t('coreHelp:createHelpPostFailed'),
          eventType: EventType.create,
        },
      }))
      console.error('error in watchCreateEditorHelpPostInitiated: ', e)
    }
  }
}

// deleteEditorHelpPost
const deleteEditorHelpPostWorker = bindAsyncAction(deleteEditorHelpPost)(
  function * (params: DeleteEditorHelpPostRequest) {
    return yield call([EditorHelpRESTServiceApi, EditorHelpRESTServiceApi.deleteEditorHelpPost], params)
  },
)

function * watchDeleteEditorHelpPostInitiated () {
  while (true) {
    const action: Action<DeleteEditorHelpPostRequest> = yield take(deleteEditorHelpPostInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield deleteEditorHelpPostWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        message: {
          severity: Severity.success,
          message: i18n.t('coreHelp:deleteHelpPostSuccess'),
          eventType: EventType.delete,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        message: {
          severity: Severity.error,
          message: i18n.t('coreHelp:deleteHelpPostFailed'),
          eventType: EventType.delete,
        },
      }))
      console.error('error in watchDeleteEditorHelpPostInitiated: ', e)
    }
  }
}

// getEditorHelpPost
const getEditorHelpPostWorker = bindAsyncAction(getEditorHelpPost)(
  function * (params: GetEditorHelpPostRequest) {
    return yield call([EditorHelpRESTServiceApi, EditorHelpRESTServiceApi.getEditorHelpPost], params)
  },
)

function * watchGetEditorHelpPostInitiated () {
  while (true) {
    const action: Action<GetEditorHelpPostRequest> = yield take(getEditorHelpPostInitiated)
    try {
      yield getEditorHelpPostWorker(action.payload)
    } catch (e) {
      console.error('error in watchGetHelpPostInitiated: ', e)
    }
  }
}

// updateEditorHelpPost
const updateEditorHelpPostWorker = bindAsyncAction(updateEditorHelpPost)(
  function * (params: UpdateEditorHelpPostRequest) {
    return yield call([EditorHelpRESTServiceApi, EditorHelpRESTServiceApi.updateEditorHelpPost], params)
  },
)

function * watchUpdateEditorHelpPostInitiated () {
  while (true) {
    const action: Action<UpdateEditorHelpPostRequest> = yield take(updateEditorHelpPostInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield updateEditorHelpPostWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        message: {
          severity: Severity.success,
          message: i18n.t('coreHelp:updateHelpPostSuccess'),
          eventType: EventType.update,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        message: {
          severity: Severity.error,
          message: i18n.t('coreHelp:updateHelpPostFailed'),
          eventType: EventType.update,
        },
      }))
      console.error('error in watchUpdateHelpPostInitiated: ', e)
    }
  }
}

export function * helpSagas () {
  yield fork(watchGetHelpPostInitiated)
  yield fork(watchGetHelpPostListInitiated)
  yield fork(watchCreateEditorHelpPostInitiated)
  yield fork(watchDeleteEditorHelpPostInitiated)
  yield fork(watchGetEditorHelpPostInitiated)
  yield fork(watchUpdateEditorHelpPostInitiated)
}
